import { Box, Slider, Stack, Typography } from '@mui/material'
import DataLineChart from './DataLineChart'
import { IChartPackets } from '../../services/transformLogToChartPacket'
import { useMemo, useRef, useState } from 'react'
import { sampleArray } from '../../services/utils'
import { spaceColors } from '../../theme'

// max recommended count of packets to display on the chart is 10000
const MAX_PACKETS_COUNT = 10000
const DEBOUNCE_TIMEOUT = 500

interface IPacketChartsProps extends IChartPackets {
    timestampRange: [number, number]
}

export default function PacketsCharts({
    dpr,
    tankPressures,
    ticks,
    modes,
    timestampRange,
    magnitudes,
}: IPacketChartsProps) {
    const [brushTimestamps, setBrushTimestamps] = useState<[number, number]>(timestampRange)
    const [sampleRate, setSampleRate] = useState<number>()
    const [sliceIndexes, setSliceIndexes] = useState<[number, number]>([0, 0])
    const [hoverTimestamp, setHoverTimestamp] = useState<number>()

    const onBrushChange = (newValues: number[]) => {
        if (newValues[0] === brushTimestamps[0] && newValues[1] === brushTimestamps[1]) return

        setBrushTimestamps([newValues[0], newValues[1]])
    }

    const onTimestamp = (timestamp: number) => {
        console.log('timestamp: ', timestamp)
        setHoverTimestamp(timestamp)
    }

    const sampledPackets = useMemo(() => {
        let startIndex = dpr.findIndex((packet) => packet.timestamp === brushTimestamps[0])
        let endIndex = dpr.findIndex((packet) => packet.timestamp === brushTimestamps[1])

        // the timestamps are not discrete/constant so we might get a timestamp that is not in the packets
        // we need to find the closest timestamp to the one we have
        if (startIndex === -1) {
            console.log('startIndex.not-found: ', startIndex)
            startIndex = dpr.reduce((prev, curr, index) => {
                return Math.abs(curr.timestamp - brushTimestamps[0]) <
                    Math.abs(dpr[prev].timestamp - brushTimestamps[0])
                    ? index
                    : prev
            }, 0)
            console.log('closest startIndex: ', startIndex)
        }

        if (endIndex === -1) {
            console.log('endIndex.not-found: ', endIndex)
            endIndex = dpr.reduce((prev, curr, index) => {
                return Math.abs(curr.timestamp - brushTimestamps[1]) <
                    Math.abs(dpr[prev].timestamp - brushTimestamps[1])
                    ? index
                    : prev
            }, 0)
            console.log('closest endIndex: ', endIndex)
        }

        if (startIndex === sliceIndexes[0] && endIndex === sliceIndexes[1]) {
            // TODO: add a notification to say that the chart is not updating because the startIndex/endIndex are the same
            // TODO: since there can be a whole in the packets
        }

        setSliceIndexes([startIndex, endIndex])
        const newSampleRate = Math.ceil((endIndex - startIndex) / MAX_PACKETS_COUNT)

        if (newSampleRate !== sampleRate && newSampleRate > 0) {
            setSampleRate(newSampleRate)
            return {
                dpr: sampleArray(dpr.slice(startIndex, endIndex), newSampleRate),
                tankPressures: sampleArray(tankPressures.slice(startIndex, endIndex), newSampleRate),
                ticks: sampleArray(ticks.slice(startIndex, endIndex), newSampleRate),
                modes: sampleArray(modes.slice(startIndex, endIndex), newSampleRate),
            }
        } else {
            return {
                dpr: dpr.slice(startIndex, endIndex),
                tankPressures: tankPressures.slice(startIndex, endIndex),
                ticks: ticks.slice(startIndex, endIndex),
                modes: modes.slice(startIndex, endIndex),
            }
        }
    }, [brushTimestamps, dpr, tankPressures, ticks, modes])

    return (
        <Box sx={{ width: '100%', backgroundColor: 'white', overflow: 'scroll', m: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
                <Typography variant='body1' sx={{ px: 1 }}>
                    <strong>DPR: </strong> {sampledPackets.dpr.length}/{dpr.length}
                    &nbsp;&nbsp;&nbsp;
                    <strong>Tank Pressures: </strong> {sampledPackets.tankPressures.length}/{tankPressures.length}
                    &nbsp;&nbsp;&nbsp;
                    <strong>Ticks: </strong>
                    {sampledPackets.ticks.length}/{ticks.length}
                    &nbsp;&nbsp;&nbsp;
                    <strong>Modes: </strong>
                    {sampledPackets.modes.length}/{modes.length}
                </Typography>
                <Typography variant='body1' sx={{ px: 1 }}>
                    <strong>Sample Rate: </strong> {sampleRate}
                </Typography>
                <Typography variant='body1' sx={{ px: 1 }}>
                    <strong>Range: </strong> {new Date(brushTimestamps[0]).toLocaleTimeString()} -{' '}
                    {new Date(brushTimestamps[1]).toLocaleTimeString()}
                </Typography>
            </Box>

            <RangeSlider
                start={timestampRange[0]}
                end={timestampRange[1]}
                onChange={onBrushChange}
                hoverTimestamp={hoverTimestamp}
            />
            <Stack direction='row' flexWrap='wrap' spacing={1}>
                {sampledPackets.dpr.length > 0 && (
                    <DataLineChart
                        title='DPR'
                        syncId='line-charts'
                        data={sampledPackets.dpr}
                        yAxisDataKeys={[{ dataKey: 'dpr', lineColor: spaceColors.blue }]}
                        magnitudes={magnitudes}
                        onTimestamp={onTimestamp}
                    />
                )}
                {sampledPackets.tankPressures.length > 0 && (
                    <DataLineChart
                        title='Tank Pressures'
                        syncId='line-charts'
                        data={sampledPackets.tankPressures}
                        yAxisDataKeys={[
                            { dataKey: 'lox', lineColor: spaceColors.green },
                            { dataKey: 'fuel', lineColor: spaceColors.coral },
                        ]}
                        magnitudes={magnitudes}
                        onTimestamp={onTimestamp}
                    />
                )}
                {sampledPackets.ticks.length > 0 && (
                    <DataLineChart
                        title='Ticks'
                        syncId='line-charts'
                        data={sampledPackets.ticks}
                        yAxisDataKeys={[
                            { dataKey: 'lox', lineColor: spaceColors.blue },
                            { dataKey: 'fuel', lineColor: spaceColors.coral },
                        ]}
                        magnitudes={magnitudes}
                        onTimestamp={onTimestamp}
                    />
                )}
                {sampledPackets.modes.length > 0 && (
                    <DataLineChart
                        title='Modes'
                        syncId='line-charts'
                        data={sampledPackets.modes}
                        yAxisDataKeys={[
                            { dataKey: 'lox', lineColor: spaceColors.blue },
                            { dataKey: 'fuel', lineColor: spaceColors.coral },
                        ]}
                        magnitudes={magnitudes}
                        onTimestamp={onTimestamp}
                    />
                )}
            </Stack>
        </Box>
    )
}

function valuetext(start: number, value: number) {
    return `${value} (${new Date(value - start).toLocaleTimeString()})`
}

interface IRangeSliderProps {
    start: number
    end: number
    onChange: (value: number[]) => void
    hoverTimestamp?: number
}

export function RangeSlider({ start, end, onChange, hoverTimestamp }: IRangeSliderProps) {
    const [value, setValue] = useState<[number, number]>([start, end])
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

    const marks = useMemo(() => {
        const marks = [
            { value: start, label: new Date(start).toLocaleTimeString() },
            { value: end, label: new Date(end).toLocaleTimeString() },
        ]

        if (hoverTimestamp && hoverTimestamp > start && hoverTimestamp < end) {
            marks.push({ value: hoverTimestamp, label: new Date(hoverTimestamp).toLocaleTimeString() })
        }

        return marks
    }, [start, end, hoverTimestamp])

    const handleChange = (event: Event, newValue: number | number[]) => {
        setValue(newValue as [number, number])

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current)
        }

        debounceTimeout.current = setTimeout(() => {
            onChange(value)
            debounceTimeout.current = null // Clear the ref once the timeout is done
        }, DEBOUNCE_TIMEOUT)
    }

    return (
        <Box sx={{ px: 6, m: 2 }}>
            <Slider
                value={value}
                onChange={handleChange}
                valueLabelDisplay='auto'
                min={start}
                max={end}
                step={20}
                marks={marks}
                valueLabelFormat={(value) => valuetext(start, value as number)}
            />
        </Box>
    )
}
